import { Dayjs } from "dayjs";
import {
  PricebookApplyMode,
  PricebookApplyTiming,
  PricebookDraftDiff,
} from "../../../../types/price-book.type";
import { numberWithCommas } from "../../../../utils";
import {
  PERIOD_MESSAGE,
  formatPeriodDate,
  formatPeriodDay,
  formatPeriodRange,
} from "../Periods/periodModel";

/** The apply dialog's timing: the mode and the days it needs. */
export interface ApplyTimingForm {
  mode: PricebookApplyMode;
  effectiveDate: Dayjs | null;
  startDate: Dayjs | null;
  endDate: Dayjs | null;
}

export type ApplyTimingErrors = Partial<
  Record<"mode" | "effectiveDate" | "startDate" | "endDate", string>
>;

export const INITIAL_APPLY_TIMING: ApplyTimingForm = {
  mode: "now",
  effectiveDate: null,
  startDate: null,
  endDate: null,
};

export const APPLY_MODES: PricebookApplyMode[] = ["now", "date", "range"];

export const APPLY_MODE_LABEL: Record<PricebookApplyMode, string> = {
  now: "ทันที",
  date: "ตั้งวันเริ่มใช้",
  range: "ช่วงเวลา (เริ่ม–สิ้นสุด)",
};

/** The handbook's "ตั้งเวลา apply" table, one line per mode. */
export const APPLY_MODE_HINT: Record<PricebookApplyMode, string> = {
  now: "เขียนทับต้นฉบับตอนกดยืนยัน",
  date: "ราคาใหม่เป็นราคาหลักตั้งแต่ 00:00 เวลาไทยของวันที่เลือก ไม่มีวันสิ้นสุด ยกเลิกได้ก่อนถึงวัน ถ้าต้นฉบับถูกแก้ก่อนถึงวัน ระบบจะไม่นำไปใช้",
  range: "รายการที่ราคาเปลี่ยนกลายเป็นช่วงราคาของต้นฉบับ หมดช่วงแล้วกลับไปขายที่ราคาเดิม",
};

export const APPLY_CONFIRM_LABEL: Record<PricebookApplyMode, string> = {
  now: "นำไปใช้ทันที",
  date: "ตั้งเวลา",
  range: "ตั้งช่วงราคา",
};

/** Worded as the API words the same refusals. */
export const APPLY_TIMING_MESSAGE = {
  effectiveDateRequired: "กรุณาเลือกวันเริ่มใช้",
  effectiveDateNotFuture: "วันที่เริ่มใช้ต้องหลังวันนี้",
} as const;

/** Narrows a radio value back to an apply mode; anything else is not one. */
export const toApplyMode = (value: string): PricebookApplyMode | undefined =>
  APPLY_MODES.find((mode) => mode === value);

const toValidDay = (day: Dayjs | null): Dayjs | null =>
  day?.isValid() ? day : null;

const validateRange = (form: ApplyTimingForm, today: Dayjs): ApplyTimingErrors => {
  const errors: ApplyTimingErrors = {};
  const startDate = toValidDay(form.startDate);
  const endDate = toValidDay(form.endDate);
  if (!startDate) errors.startDate = PERIOD_MESSAGE.startRequired;
  else if (startDate.isBefore(today, "day")) errors.startDate = PERIOD_MESSAGE.startInPast;
  if (!endDate) errors.endDate = PERIOD_MESSAGE.endRequired;
  else if (startDate && endDate.isBefore(startDate, "day")) {
    errors.endDate = PERIOD_MESSAGE.dateOrder;
  }
  return errors;
};

/**
 * What the API would refuse about the timing, checked before the request: a start day after
 * `today` (the Bangkok day) for `date`, ordered days from today on for `range` — which is also
 * refused outright while `rangeBlockReason` says the draft cannot go as a range.
 */
export const validateApplyTiming = (
  form: ApplyTimingForm,
  today: Dayjs,
  rangeBlockReason: string | null,
): ApplyTimingErrors => {
  if (form.mode === "date") {
    const effectiveDate = toValidDay(form.effectiveDate);
    if (!effectiveDate) {
      return { effectiveDate: APPLY_TIMING_MESSAGE.effectiveDateRequired };
    }
    return effectiveDate.isAfter(today, "day")
      ? {}
      : { effectiveDate: APPLY_TIMING_MESSAGE.effectiveDateNotFuture };
  }
  if (form.mode === "range") {
    return rangeBlockReason ? { mode: rangeBlockReason } : validateRange(form, today);
  }
  return {};
};

/** The timing as the API takes it, or null while a day the mode needs is missing. */
export const buildApplyTiming = (form: ApplyTimingForm): PricebookApplyTiming | null => {
  if (form.mode === "now") return { mode: "now" };
  if (form.mode === "date") {
    const effectiveDate = toValidDay(form.effectiveDate);
    return effectiveDate
      ? { mode: "date", effective_date: formatPeriodDay(effectiveDate) }
      : null;
  }
  const startDate = toValidDay(form.startDate);
  const endDate = toValidDay(form.endDate);
  if (!startDate || !endDate) return null;
  return {
    mode: "range",
    start_date: formatPeriodDay(startDate),
    end_date: formatPeriodDay(endDate),
  };
};

/** When the prices change, for the status chip; the mode's name until its days are picked. */
export const describeApplyTiming = (form: ApplyTimingForm): string => {
  const timing = buildApplyTiming(form);
  if (!timing) return APPLY_MODE_LABEL[form.mode];
  if (timing.mode === "now") return "มีผลทันที";
  if (timing.mode === "date") return `มีผล ${formatPeriodDate(timing.effective_date)}`;
  return `ช่วง ${formatPeriodRange(timing.start_date, timing.end_date)}`;
};

/** When the source's shops get the draft's prices; the picked days, or a placeholder until then. */
const describeReach = (shops: string, form: ApplyTimingForm): string => {
  const timing = buildApplyTiming(form);
  if (form.mode === "date") {
    const day =
      timing?.mode === "date" ? formatPeriodDate(timing.effective_date) : "วันที่เลือก";
    return `ราคาใหม่มีผลตั้งแต่ ${day} กับ${shops} ร่างนี้ปิดทันทีที่ยืนยัน`;
  }
  if (form.mode === "range") {
    const days =
      timing?.mode === "range"
        ? formatPeriodRange(timing.start_date, timing.end_date)
        : "ช่วงที่เลือก";
    return `ราคาในร่างใช้กับ${shops} เฉพาะ ${days} หมดช่วงกลับไปขายที่ราคาเดิม`;
  }
  return `ราคาใหม่มีผลทันทีกับ${shops}`;
};

/** Who gets the draft's prices, from when, and where its test shops go (they leave on confirm). */
export const buildApplyImpactMessage = (
  diff: PricebookDraftDiff,
  form: ApplyTimingForm,
): string => {
  const sourceCode = diff.source.code;
  const shops = `ร้านค้า ${numberWithCommas(diff.affected_shop_count)} ร้านใน ${numberWithCommas(
    diff.affected_branch_count,
  )} สาขาที่ใช้ ${sourceCode}`;
  const reach = describeReach(shops, form);
  if (diff.draft_shop_count === 0) return reach;
  return `${reach} · ร้านทดสอบ ${numberWithCommas(
    diff.draft_shop_count,
  )} ร้านกลับไปใช้ ${sourceCode}`;
};

/** The success notice: what was done to the source, and when it takes effect. */
export const buildApplySuccessMessage = (
  sourceCode: string,
  timing: PricebookApplyTiming,
): string => {
  if (timing.mode === "date") {
    return `ตั้งเวลานำร่างไปใช้กับ ${sourceCode} วันที่ ${formatPeriodDate(timing.effective_date)} แล้ว`;
  }
  if (timing.mode === "range") {
    return `ตั้งช่วงราคาให้ ${sourceCode} ${formatPeriodRange(timing.start_date, timing.end_date)} แล้ว`;
  }
  return `นำร่างไปใช้กับ ${sourceCode} แล้ว`;
};
